import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatManilaDate } from '@dispatchdash/shared';
import {
  tripsApi,
  vehiclesApi,
  vehicleRunsApi,
  type Trip,
  type Vehicle,
  type VehicleRun,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { Layout } from '@/components/Layout';
import { AttentionBar } from '@/components/AttentionBar';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { HeldCargoPanel } from '@/components/HeldCargoPanel';
import { Input } from '@/components/Input';
import { internalNavItems } from '@/pages/navItems';
import { useToast } from '@/components/Feedback';
import { formatDate, humanize, vehicleRunLabel } from '@/lib/format';
import { dropTripOnTarget, moveTripInOrder, slotsFromOrderedTrips, tripCanDrag, tripCanDropOn, tripCanMove } from '@/lib/runQueueReorder';
import { tripIdFromPointInRun } from '@/lib/runQueuePointerDrag';
const KANBAN_COLUMNS = [
  { key: 'requested', label: 'Requested', statuses: ['requested'] },
  { key: 'confirmed', label: 'Confirmed', statuses: ['confirmed'] },
  { key: 'dispatched', label: 'Dispatched', statuses: ['dispatched'] },
  { key: 'executing', label: 'Executing', statuses: ['executing'] },
  { key: 'on_hold', label: 'On Hold', statuses: ['on_hold'] },
  { key: 'completed', label: 'Completed', statuses: ['completed', 'ready_to_bill'] },
];

const RELEASE_DRAG_TYPE = 'application/x-dispatchdash-trip';

/** In-progress trips from earlier days stay on today's board so nothing running drops out of sight. */
const CARRY_OVER_STATUSES = ['dispatched', 'executing', 'on_hold'];

function tripBoardDate(trip: Trip): string | null {
  const raw = trip.scheduledDate ?? trip.createdAt;
  if (!raw) return null;
  return formatManilaDate(new Date(raw));
}

function parseVehicleIds(raw: string | null): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

export function DispatchDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const canRelease = hasPermission('trips.dispatch');
  const canReorder = hasPermission('trips.dispatch');
  const toast = useToast();

  const todayDate = formatManilaDate(new Date());
  const boardDate = searchParams.get('date') || todayDate;
  const isToday = boardDate === todayDate;
  const selectedVehicleIds = useMemo(
    () => parseVehicleIds(searchParams.get('vehicles')),
    [searchParams],
  );
  const vehicleFilterActive = selectedVehicleIds.length > 0;

  const [activeFilter, setActiveFilter] = useState<string | undefined>();
  const [dragTripId, setDragTripId] = useState<string | null>(null);
  const [releaseTargetId, setReleaseTargetId] = useState<string | null>(null);
  const [boardError, setBoardError] = useState('');
  const [queueConflict, setQueueConflict] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleMenuOpen, setVehicleMenuOpen] = useState(false);
  const [heldCargoRefreshKey, setHeldCargoRefreshKey] = useState(0);
  const vehicleMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!vehicleMenuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!vehicleMenuRef.current?.contains(e.target as Node)) setVehicleMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVehicleMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [vehicleMenuOpen]);

  const { data: trips, loading, error: tripsError, reload } = useAsync(
    () => tripsApi.list(activeFilter ? { filter: activeFilter } : undefined),
    [activeFilter],
  );

  const { data: counts, reload: reloadCounts } = useAsync(() => tripsApi.attentionCounts(), []);
  const { data: vehicles } = useAsync(() => vehiclesApi.list(), []);

  const {
    data: vehicleRuns,
    loading: runsLoading,
    reload: reloadRuns,
  } = useAsync(
    () =>
      vehicleFilterActive
        ? vehicleRunsApi.list({ date: boardDate, vehicleIds: selectedVehicleIds })
        : Promise.resolve([] as VehicleRun[]),
    [vehicleFilterActive, boardDate, selectedVehicleIds.join(',')],
  );

  const attentionItems = counts
    ? [
        { key: 'unassigned', label: 'Unassigned', count: counts.unassigned, filter: 'unassigned' },
        { key: 'approval', label: 'Awaiting approval', count: counts.awaitingApproval, filter: 'awaiting_approval' },
        { key: 'overdue', label: 'Overdue', count: counts.overdue, filter: 'overdue' },
        { key: 'hold', label: 'On hold', count: counts.onHold, filter: 'on_hold' },
        { key: 'ready', label: 'Ready to dispatch', count: counts.readyToDispatch, filter: 'ready_to_dispatch' },
      ]
    : [];

  const fleet = Array.isArray(vehicles) ? vehicles : [];
  const allTrips = Array.isArray(trips) ? trips : [];

  const filteredTrips = useMemo(() => {
    let rows = allTrips;
    if (vehicleFilterActive) {
      const set = new Set(selectedVehicleIds);
      rows = rows.filter((t) => t.vehicleId && set.has(t.vehicleId));
    }
    // Date filter: the board always shows the chosen Manila day (today by default).
    // Attention filters (Overdue, Unassigned…) span all dates unless a date was picked explicitly.
    if (!activeFilter || searchParams.get('date')) {
      rows = rows.filter((t) => {
        const day = tripBoardDate(t);
        if (!day) return false;
        if (day === boardDate) return true;
        // Keep earlier trips that are still running visible on today's board.
        return isToday && day < boardDate && CARRY_OVER_STATUSES.includes(t.status);
      });
    }
    return rows;
  }, [allTrips, vehicleFilterActive, selectedVehicleIds, searchParams, boardDate, activeFilter, isToday]);

  const showingAllDates = Boolean(activeFilter) && !searchParams.get('date');

  const refreshBoard = () => {
    void reload();
    void reloadCounts();
    setHeldCargoRefreshKey((k) => k + 1);
    if (vehicleFilterActive) void reloadRuns();
  };

  const updateUrl = useCallback(
    (next: { vehicles?: string[]; date?: string }) => {
      const params = new URLSearchParams(searchParams);
      const vehicles = next.vehicles ?? selectedVehicleIds;
      const date = next.date ?? boardDate;
      if (vehicles.length) params.set('vehicles', vehicles.join(','));
      else params.delete('vehicles');
      if (date && date !== formatManilaDate(new Date())) params.set('date', date);
      else if (vehicles.length) params.set('date', date);
      else params.delete('date');
      setSearchParams(params, { replace: true });
    },
    [searchParams, selectedVehicleIds, boardDate, setSearchParams],
  );

  const toggleVehicle = (id: string) => {
    const set = new Set(selectedVehicleIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    updateUrl({ vehicles: [...set] });
  };

  const clearFilters = () => {
    setSearchParams({}, { replace: true });
    setVehicleSearch('');
    setQueueConflict('');
  };

  const vehicleOptions = useMemo(() => {
    const q = vehicleSearch.trim().toLowerCase();
    return fleet.filter((v) => {
      if (!q) return true;
      const plate = (v.plate || v.plateNumber || '').toLowerCase();
      const driver = (v.driverName || '').toLowerCase();
      const type = (v.vehicleTypeCode || v.vehicleTypeName || v.type || '').toLowerCase();
      return plate.includes(q) || driver.includes(q) || type.includes(q) || v.id.includes(q);
    });
  }, [fleet, vehicleSearch]);

  const handleRelease = async (tripId: string) => {
    setReleaseTargetId(tripId);
    setBoardError('');
    try {
      await tripsApi.release(tripId);
      const t = allTrips.find((x) => x.id === tripId);
      toast(`${t ? tripLabel(t) : 'Trip'} dispatched to the crew`);
      void reload();
      void reloadCounts();
      if (vehicleFilterActive) reloadRuns();
    } catch (err) {
      setBoardError((err as { message?: string }).message ?? 'Failed to dispatch trip');
    } finally {
      setReleaseTargetId(null);
    }
  };

  const handleDragStart = (event: DragEvent, trip: Trip) => {
    if (!canRelease || trip.status !== 'confirmed') return;
    event.dataTransfer.setData(RELEASE_DRAG_TYPE, trip.id);
    event.dataTransfer.effectAllowed = 'move';
    setDragTripId(trip.id);
  };

  const handleDragEnd = () => {
    setDragTripId(null);
  };

  const handleDropOnDispatched = async (event: DragEvent) => {
    event.preventDefault();
    if (!canRelease) return;
    const tripId = event.dataTransfer.getData(RELEASE_DRAG_TYPE);
    setDragTripId(null);
    if (!tripId) return;
    const trip = filteredTrips.find((t) => t.id === tripId);
    if (!trip || trip.status !== 'confirmed') return;
    await handleRelease(tripId);
  };

  const plateFor = (vehicleId: string) => {
    const v = fleet.find((x) => x.id === vehicleId);
    return v?.plate || v?.plateNumber || vehicleId.slice(0, 8);
  };

  return (
    <Layout
      navItems={internalNavItems}
      rightRail={<FleetStrip vehicles={fleet} />}
    >
      <div className="page">
        <div className="page-header row-between">
          <div>
            <h1>Dispatch board</h1>
            <p className="text-muted">
              {showingAllDates ? (
                'Showing matching trips across all dates'
              ) : (
                <>
                  Operations for{' '}
                  {new Date(`${boardDate}T12:00:00+08:00`).toLocaleDateString('en-PH', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'Asia/Manila',
                  })}
                  {isToday ? ' (today)' : ''}
                </>
              )}
            </p>
            {canRelease && (
              <p className="text-muted" style={{ fontSize: '0.8125rem', marginTop: 'var(--space-xs)' }}>
                Drag confirmed trips to Dispatched, or use Dispatch on the card.
              </p>
            )}
          </div>
          <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--space-sm)', alignItems: 'flex-end' }}>
            <div className="vehicle-filter" ref={vehicleMenuRef} style={{ position: 'relative', minWidth: 220 }}>
              <span id="vehicle-filter-label" className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 4 }}>
                Vehicles
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                style={{ width: '100%', justifyContent: 'space-between' }}
                aria-haspopup="listbox"
                aria-expanded={vehicleMenuOpen}
                aria-labelledby="vehicle-filter-label"
                onClick={() => setVehicleMenuOpen((o) => !o)}
              >
                {vehicleFilterActive
                  ? `${selectedVehicleIds.length} selected`
                  : 'All vehicles'}
              </button>
              {vehicleMenuOpen && (
                <div className="vehicle-filter-menu" role="listbox" aria-multiselectable="true">
                  <Input
                    id="vehicle-filter-search"
                    aria-label="Search vehicles"
                    placeholder="Search plate, driver, type…"
                    value={vehicleSearch}
                    onChange={(e) => setVehicleSearch(e.target.value)}
                    autoFocus
                  />
                  <div className="vehicle-filter-options">
                    {vehicleOptions.length === 0 ? (
                      <p className="text-muted" style={{ fontSize: '0.8125rem', padding: '0.5rem' }}>
                        No matches
                      </p>
                    ) : (
                      vehicleOptions.map((v) => {
                        const checked = selectedVehicleIds.includes(v.id);
                        return (
                          <label key={v.id} className="vehicle-filter-option">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleVehicle(v.id)}
                            />
                            <span>
                              <strong>{v.plate || v.plateNumber || '—'}</strong>
                              <span className="text-muted">
                                {' '}
                                · {v.driverName ?? 'No driver'} · {v.vehicleTypeCode ?? v.type ?? ''}
                              </span>
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <div className="row" style={{ gap: '0.5rem', padding: '0.5rem' }}>
                    <Button type="button" size="sm" variant="secondary" onClick={() => updateUrl({ vehicles: [] })}>
                      All vehicles
                    </Button>
                    <Button type="button" size="sm" variant="secondary" onClick={() => setVehicleMenuOpen(false)}>
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <label>
              <span className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 4 }}>
                Date
              </span>
              <input
                type="date"
                className="input"
                value={boardDate}
                onChange={(e) => updateUrl({ date: e.target.value || formatManilaDate(new Date()) })}
              />
            </label>
            {(vehicleFilterActive || searchParams.get('date')) && (
              <Button type="button" variant="secondary" onClick={clearFilters}>Clear filters</Button>
            )}
            <Button type="button" variant="secondary" onClick={refreshBoard}>
              Refresh
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/app/dispatch/book/bulk')}>
              Bulk upload
            </Button>
            <Button type="button" onClick={() => navigate('/app/dispatch/book')}>New booking</Button>
          </div>
        </div>

        {(boardError || queueConflict) && (
          <p
            className="form-error"
            role="alert"
            data-testid="dispatch-queue-message"
            style={{ marginBottom: 'var(--space-md)' }}
          >
            {queueConflict || boardError}
          </p>
        )}

        <AttentionBar
          items={attentionItems}
          activeFilter={activeFilter}
          onFilter={setActiveFilter}
        />

        <HeldCargoPanel refreshKey={heldCargoRefreshKey} onReleased={() => { void reload(); void reloadCounts(); }} />

        {vehicleFilterActive && (
          <VehicleRunQueueSection
            runs={Array.isArray(vehicleRuns) ? vehicleRuns : []}
            loading={runsLoading}
            canReorder={canReorder}
            plateFor={plateFor}
            onConflict={(msg) => {
              setQueueConflict(msg);
              reloadRuns();
            }}
            onReordered={() => {
              setQueueConflict('');
              reloadRuns();
            }}
            onOpenTrip={(id) => navigate(`/app/dispatch/trips/${id}`)}
          />
        )}

        {loading && allTrips.length === 0 ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : tripsError ? (
          <div className="empty-state" role="alert">
            <h3>Could not load the board</h3>
            <p>{tripsError}</p>
            <Button type="button" onClick={refreshBoard}>Retry</Button>
          </div>
        ) : filteredTrips.length === 0 && vehicleFilterActive ? (
          <div className="empty-state">
            <h3>No trips for selected vehicles</h3>
            <p>Filtering the board does not change run sequences. Clear filters to see all trips.</p>
            <Button type="button" onClick={clearFilters}>Clear filters</Button>
          </div>
        ) : filteredTrips.length === 0 && !activeFilter ? (
          <div className="empty-state">
            <h3>No trips {isToday ? 'today' : `on ${formatDate(boardDate)}`}</h3>
            <p>Pick another date above, create a booking, or wait for client portal requests to appear here.</p>
            <Button type="button" onClick={() => navigate('/app/dispatch/book')}>Create booking</Button>
          </div>
        ) : (
          <div className="kanban">
            {KANBAN_COLUMNS.map((col) => {
              const colTrips = filteredTrips.filter((t) => col.statuses.includes(t.status));
              const isDropTarget = canRelease && col.key === 'dispatched';
              return (
                <div
                  key={col.key}
                  className={`kanban-col${isDropTarget && dragTripId ? ' kanban-col-drop' : ''}`}
                  onDragOver={isDropTarget ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                  onDrop={isDropTarget ? handleDropOnDispatched : undefined}
                >
                  <div className="kanban-header">
                    <span>{col.label}</span>
                    <span className="kanban-count">{colTrips.length}</span>
                  </div>
                  <div className="kanban-cards">
                    {colTrips.map((trip) => (
                      <TripCard
                        key={trip.id}
                        trip={trip}
                        draggable={canRelease && trip.status === 'confirmed'}
                        releasing={releaseTargetId === trip.id}
                        onRelease={canRelease && trip.status === 'confirmed' ? () => handleRelease(trip.id) : undefined}
                        onDragStart={(e) => handleDragStart(e, trip)}
                        onDragEnd={handleDragEnd}
                        onClick={() => navigate(`/app/dispatch/trips/${trip.id}`)}
                        carriedOverFrom={(() => {
                          const day = tripBoardDate(trip);
                          return !showingAllDates && day && day !== boardDate ? day : null;
                        })()}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        .kanban {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: var(--space-md);
          min-height: 400px;
        }
        .kanban-col {
          background: var(--color-sand);
          border-radius: var(--radius-md);
          min-width: 0;
        }
        .kanban-col-drop {
          outline: 2px dashed var(--color-forest);
          outline-offset: -2px;
        }
        .kanban-header {
          display: flex;
          justify-content: space-between;
          padding: var(--space-sm) var(--space-md);
          font-weight: 600;
          font-size: 0.8125rem;
          color: var(--color-charcoal-light);
          border-bottom: 1px solid var(--color-border);
        }
        .kanban-count {
          background: var(--color-surface);
          padding: 0 6px;
          border-radius: 999px;
          font-size: 0.75rem;
        }
        .kanban-cards {
          padding: var(--space-sm);
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
          min-height: 120px;
        }
        @media (max-width: 768px) {
          /* Stacked columns on phones: don't reserve tall blank space for empty ones. */
          .kanban { min-height: 0; }
          .kanban-cards { min-height: 48px; }
        }
        .trip-card {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          padding: var(--space-sm) var(--space-md);
          cursor: pointer;
          transition: border-color 0.15s;
        }
        .trip-card[draggable="true"] {
          cursor: grab;
        }
        .trip-card[draggable="true"]:active {
          cursor: grabbing;
        }
        .trip-card:hover,
        .trip-card:focus-visible {
          border-color: var(--color-forest);
        }
        .trip-card:focus-visible {
          outline: 2px solid var(--color-forest);
          outline-offset: 1px;
        }
        .trip-card.urgent { border-left: 3px solid var(--color-urgent); }
        .trip-card.warning { border-left: 3px solid var(--color-warning); }
        .trip-ref {
          font-weight: 600;
          font-size: 0.875rem;
        }
        .trip-client {
          font-size: 0.75rem;
          color: var(--color-text-muted);
        }
        .trip-route {
          font-size: 0.75rem;
          margin-top: var(--space-xs);
        }
        .trip-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: var(--space-sm);
        }
        .trip-actions {
          margin-top: var(--space-sm);
        }
        .fleet-strip h4 {
          font-size: 0.8125rem;
          margin-bottom: var(--space-md);
          color: var(--color-charcoal-light);
        }
        .fleet-item {
          padding: var(--space-sm);
          border-bottom: 1px solid var(--color-border);
          font-size: 0.8125rem;
        }
        .fleet-plate { font-weight: 600; }
        .fleet-status { color: var(--color-text-muted); font-size: 0.75rem; }
        .vehicle-filter-menu {
          position: absolute;
          z-index: 20;
          top: 100%;
          left: 0;
          right: 0;
          margin-top: 4px;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          max-height: 320px;
          display: flex;
          flex-direction: column;
        }
        .vehicle-filter-options {
          overflow: auto;
          max-height: 200px;
        }
        .vehicle-filter-option {
          display: flex;
          gap: 0.5rem;
          align-items: flex-start;
          padding: 0.4rem 0.75rem;
          font-size: 0.8125rem;
          cursor: pointer;
        }
        .vehicle-filter-option:hover { background: var(--color-sand); }
        .run-queue {
          margin-bottom: var(--space-lg);
          padding: var(--space-md);
          background: var(--color-sand);
          border-radius: var(--radius-md);
        }
        .run-queue h2 {
          font-size: 1rem;
          margin: 0 0 var(--space-sm);
        }
        .run-queue-vehicle {
          margin-bottom: var(--space-md);
        }
        .run-queue-vehicle h3 {
          font-size: 0.875rem;
          margin: 0 0 var(--space-sm);
        }
        .run-queue-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: var(--space-xs);
        }
        .run-queue-item {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          padding: var(--space-sm) var(--space-md);
        }
        .run-queue-item.dragging {
          opacity: 0.6;
        }
        .run-queue-item.drop-target {
          outline: 2px dashed var(--color-forest);
        }
        .run-queue-seq {
          font-weight: 600;
          font-variant-numeric: tabular-nums;
          min-width: 1.5rem;
        }
        .run-queue-body {
          flex: 1;
          min-width: 0;
          cursor: pointer;
        }
        .run-queue-controls {
          display: flex;
          gap: 0.25rem;
        }
        .run-queue-drag-handle {
          flex: 0 0 auto;
          width: 1.75rem;
          height: 1.75rem;
          padding: 0;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          background: var(--color-fog);
          color: var(--color-charcoal-light);
          cursor: grab;
          font-size: 0.875rem;
          line-height: 1;
          touch-action: none;
          user-select: none;
        }
        .run-queue-drag-handle:active {
          cursor: grabbing;
        }
        .run-queue-drag-handle:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .run-queue-drag-handle--locked {
          cursor: default;
          opacity: 0.4;
          pointer-events: none;
        }
      `}</style>
    </Layout>
  );
}

function VehicleRunQueueSection({
  runs,
  loading,
  canReorder,
  plateFor,
  onConflict,
  onReordered,
  onOpenTrip,
}: {
  runs: VehicleRun[];
  loading: boolean;
  canReorder: boolean;
  plateFor: (vehicleId: string) => string;
  onConflict: (message: string) => void;
  onReordered: () => void;
  onOpenTrip: (tripId: string) => void;
}) {
  return (
    <section className="run-queue" aria-label="Vehicle run queue">
      <h2>Vehicle run queue</h2>
      <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: 'var(--space-md)' }}>
        Drag or use Up/Down to set the order this truck runs its trips.
      </p>
      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : runs.length === 0 ? (
        <p className="text-muted">No vehicle runs for this date and selection.</p>
      ) : (
        runs.map((run) => (
          <VehicleRunQueue
            key={run.id}
            run={run}
            plate={plateFor(run.vehicleId)}
            canReorder={canReorder}
            onConflict={onConflict}
            onReordered={onReordered}
            onOpenTrip={onOpenTrip}
          />
        ))
      )}
    </section>
  );
}

function VehicleRunQueue({
  run,
  plate,
  canReorder,
  onConflict,
  onReordered,
  onOpenTrip,
}: {
  run: VehicleRun;
  plate: string;
  canReorder: boolean;
  onConflict: (message: string) => void;
  onReordered: () => void;
  onOpenTrip: (tripId: string) => void;
}) {
  const [orderedIds, setOrderedIds] = useState(() => run.trips.map((t) => t.tripId));
  const [version, setVersion] = useState(run.version);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);
  const dragIdRef = useRef<string | null>(null);
  const dropIdRef = useRef<string | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  /** Which input path owns the active drag: pointer wins; mouse is fallback. */
  const inputKindRef = useRef<'pointer' | 'mouse' | null>(null);
  const captureElRef = useRef<HTMLElement | null>(null);
  // Always call persist with the version currently rendered (avoids stale closures).
  const versionRef = useRef(version);
  versionRef.current = version;
  const orderedIdsRef = useRef(orderedIds);
  orderedIdsRef.current = orderedIds;
  const runIdRef = useRef(run.id);
  runIdRef.current = run.id;

  useEffect(() => {
    setOrderedIds(run.trips.map((t) => t.tripId));
    setVersion(run.version);
  }, [run]);

  const tripById = useMemo(() => {
    const map = new Map(run.trips.map((t) => [t.tripId, t]));
    return map;
  }, [run.trips]);

  const statusByTripId = useMemo(() => {
    const map = new Map<string, string>();
    for (const slot of run.trips) {
      const status = slot.trip?.status ?? slot.status ?? '';
      map.set(slot.tripId, status);
    }
    return map;
  }, [run.trips]);

  const legalitySlots = useMemo(
    () => slotsFromOrderedTrips(orderedIds, statusByTripId),
    [orderedIds, statusByTripId],
  );
  const legalitySlotsRef = useRef(legalitySlots);
  legalitySlotsRef.current = legalitySlots;

  const clearPointerDragState = useCallback(() => {
    const captureEl = captureElRef.current;
    const pid = pointerIdRef.current;
    if (captureEl && pid != null) {
      try {
        if (captureEl.hasPointerCapture?.(pid)) {
          captureEl.releasePointerCapture(pid);
        }
      } catch {
        /* element may be gone */
      }
    }
    captureElRef.current = null;
    pointerIdRef.current = null;
    inputKindRef.current = null;
    dragIdRef.current = null;
    dropIdRef.current = null;
    setDragId(null);
    setDropId(null);
  }, []);
  const persistOrder = useCallback(
    async (nextIds: string[]) => {
      if (!canReorder) return;
      const expectedVersion = versionRef.current;
      setSaving(true);
      try {
        const updated = await vehicleRunsApi.reorder(run.id, {
          orderedTripIds: nextIds,
          expectedVersion,
        });
        setVersion(updated.version);
        setOrderedIds(nextIds);
        onReordered();
      } catch (err) {
        const status = (err as { status?: number }).status;
        const message =
          (err as { message?: string }).message ??
          (status === 409 ? 'Run was updated elsewhere — reloading' : 'Failed to reorder');
        onConflict(status === 409 ? `Conflict: ${message}. Queue refreshed.` : message);
        if (status !== 409) {
          setOrderedIds(run.trips.map((t) => t.tripId));
        }
        // 409: parent onConflict already reloads authoritative run; do not call
        // onReordered (that clears the conflict banner).
      } finally {
        setSaving(false);
      }
    },
    [canReorder, onConflict, onReordered, run.id, run.trips],
  );
  const persistOrderRef = useRef(persistOrder);
  persistOrderRef.current = persistOrder;
  const clearPointerDragStateRef = useRef(clearPointerDragState);
  clearPointerDragStateRef.current = clearPointerDragState;

  const move = (tripId: string, direction: -1 | 1) => {
    const slots = legalitySlotsRef.current;
    const ordered = orderedIdsRef.current;
    if (!tripCanMove(slots, ordered, tripId, direction)) return;
    const next = moveTripInOrder(ordered, tripId, direction);
    if (!next) return;
    void persistOrder(next);
  };

  const updateDropFromPoint = (clientX: number, clientY: number) => {
    const under = document.elementFromPoint(clientX, clientY);
    const overId = tripIdFromPointInRun(under, runIdRef.current);
    const legal =
      !!overId &&
      tripCanDropOn(legalitySlotsRef.current, orderedIdsRef.current, dragIdRef.current, overId);
    const nextDrop = legal ? overId : null;
    if (nextDrop !== dropIdRef.current) {
      dropIdRef.current = nextDrop;
      setDropId(nextDrop);
    }
  };

  const completeDropFromPoint = (clientX: number, clientY: number) => {
    const under = document.elementFromPoint(clientX, clientY);
    // Completion target is only the live same-run row under the release point.
    // Never fall back to dropIdRef (stale hover) — outside / other-run = cancel.
    const targetId = tripIdFromPointInRun(under, runIdRef.current);
    const movingId = dragIdRef.current;
    // Clear synchronously so a bubbled duplicate up is a no-op.
    clearPointerDragStateRef.current();
    if (!targetId || !movingId) return;
    if (!tripCanDropOn(legalitySlotsRef.current, orderedIdsRef.current, movingId, targetId)) {
      return;
    }
    const next = dropTripOnTarget(orderedIdsRef.current, targetId, movingId);
    if (!next) return;
    void persistOrderRef.current(next);
  };

  // Mount-once window listeners (pointer + mouse). Refs are authoritative; do not
  // gate installation on dragId/setState. Mouse listeners cover automation paths
  // that emit mouse events without a usable pointer gesture; pointerdown's
  // preventDefault suppresses compatibility mouse events when pointer works.
  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (inputKindRef.current !== 'pointer') return;
      if (pointerIdRef.current == null || event.pointerId !== pointerIdRef.current) return;
      updateDropFromPoint(event.clientX, event.clientY);
    };

    const onPointerUp = (event: PointerEvent) => {
      if (inputKindRef.current !== 'pointer') return;
      if (pointerIdRef.current == null || event.pointerId !== pointerIdRef.current) return;
      completeDropFromPoint(event.clientX, event.clientY);
    };

    const onPointerCancel = (event: PointerEvent) => {
      if (inputKindRef.current !== 'pointer') return;
      if (pointerIdRef.current == null || event.pointerId !== pointerIdRef.current) return;
      clearPointerDragStateRef.current();
    };

    const onMouseMove = (event: MouseEvent) => {
      if (inputKindRef.current !== 'mouse') return;
      updateDropFromPoint(event.clientX, event.clientY);
    };

    const onMouseUp = (event: MouseEvent) => {
      if (inputKindRef.current !== 'mouse') return;
      completeDropFromPoint(event.clientX, event.clientY);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      clearPointerDragStateRef.current();
    };
  }, []);

  const onHandlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>, tripId: string) => {
    if (!canReorder || saving || event.button !== 0) return;
    if (!tripCanDrag(legalitySlotsRef.current, tripId)) return;
    event.preventDefault();
    event.stopPropagation();
    // Refs first — listeners are already mounted and must see ownership immediately.
    inputKindRef.current = 'pointer';
    pointerIdRef.current = event.pointerId;
    dragIdRef.current = tripId;
    dropIdRef.current = tripId;
    captureElRef.current = event.currentTarget;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* capture optional — window listeners remain authoritative */
    }
    setDragId(tripId);
    setDropId(tripId);
  };

  // Mouse fallback: used when the gesture never produces pointer events (some
  // automation attachments). Skipped when pointer already owns the drag
  // (pointerdown preventDefault suppresses compatibility mouse events).
  const onHandleMouseDown = (event: ReactMouseEvent<HTMLButtonElement>, tripId: string) => {
    if (!canReorder || saving || event.button !== 0) return;
    if (inputKindRef.current === 'pointer') return;
    if (!tripCanDrag(legalitySlotsRef.current, tripId)) return;
    event.preventDefault();
    event.stopPropagation();
    inputKindRef.current = 'mouse';
    pointerIdRef.current = null;
    dragIdRef.current = tripId;
    dropIdRef.current = tripId;
    captureElRef.current = event.currentTarget;
    setDragId(tripId);
    setDropId(tripId);
  };

  // When capture is held, move/up/cancel are retargeted to the handle; mirror the
  // window path so capture + bubble never miss a matching gesture.
  const onHandlePointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (inputKindRef.current !== 'pointer') return;
    if (pointerIdRef.current == null || event.pointerId !== pointerIdRef.current) return;
    updateDropFromPoint(event.clientX, event.clientY);
  };

  const onHandlePointerUp = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (inputKindRef.current !== 'pointer') return;
    if (pointerIdRef.current == null || event.pointerId !== pointerIdRef.current) return;
    completeDropFromPoint(event.clientX, event.clientY);
  };

  const onHandlePointerCancel = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (inputKindRef.current !== 'pointer') return;
    if (pointerIdRef.current == null || event.pointerId !== pointerIdRef.current) return;
    clearPointerDragState();
  };

  const onHandleMouseMove = (event: ReactMouseEvent<HTMLButtonElement>) => {
    if (inputKindRef.current !== 'mouse') return;
    updateDropFromPoint(event.clientX, event.clientY);
  };

  const onHandleMouseUp = (event: ReactMouseEvent<HTMLButtonElement>) => {
    if (inputKindRef.current !== 'mouse') return;
    completeDropFromPoint(event.clientX, event.clientY);
  };

  return (
    <div className="run-queue-vehicle" data-run-id={run.id} data-run-version={version}>
      <h3>
        {plate}{' '}
        <span className="text-muted" data-testid="run-queue-run-label">
          · {vehicleRunLabel(run)}
          {saving ? ' · saving…' : ''}
        </span>
      </h3>
      {orderedIds.length === 0 ? (
        <p className="text-muted" style={{ fontSize: '0.8125rem' }}>No trips in this run.</p>
      ) : (
        <ol className="run-queue-list">
          {orderedIds.map((tripId, index) => {
            const slot = tripById.get(tripId);
            const trip = slot?.trip;
            const label =
              (trip as { tripNumber?: string } | undefined)?.tripNumber ||
              tripId.slice(0, 8);
            const status = trip?.status ?? slot?.status ?? '—';
            const draggableTrip = tripCanDrag(legalitySlots, tripId);
            const canUp = tripCanMove(legalitySlots, orderedIds, tripId, -1);
            const canDown = tripCanMove(legalitySlots, orderedIds, tripId, 1);
            return (
              <li
                key={tripId}
                data-trip-id={tripId}
                className={`run-queue-item${dragId === tripId ? ' dragging' : ''}${dropId === tripId ? ' drop-target' : ''}`}
              >
                {canReorder && draggableTrip && (
                  <button
                    type="button"
                    className="run-queue-drag-handle"
                    aria-label={`Drag ${label}`}
                    data-testid="run-queue-drag-handle"
                    disabled={saving}
                    onPointerDown={(e) => onHandlePointerDown(e, tripId)}
                    onPointerMove={onHandlePointerMove}
                    onPointerUp={onHandlePointerUp}
                    onPointerCancel={onHandlePointerCancel}
                    onMouseDown={(e) => onHandleMouseDown(e, tripId)}
                    onMouseMove={onHandleMouseMove}
                    onMouseUp={onHandleMouseUp}
                  >
                    ⋮⋮
                  </button>
                )}
                {canReorder && !draggableTrip && (
                  <span
                    className="run-queue-drag-handle run-queue-drag-handle--locked"
                    aria-hidden
                    title="Locked in sequence"
                    data-testid="run-queue-drag-locked"
                  >
                    ··
                  </span>
                )}
                <span className="run-queue-seq" aria-hidden>
                  {index + 1}
                </span>
                <div
                  className="run-queue-body"
                  onClick={() => onOpenTrip(tripId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onOpenTrip(tripId);
                    }
                  }}
                  role="link"
                  tabIndex={0}
                >
                  <div className="trip-ref">{label}</div>
                  <StatusBadge status={status} size="sm" />
                </div>
                {canReorder && (
                  <div className="run-queue-controls">
                    <Button
                      size="sm"
                      variant="secondary"
                      type="button"
                      aria-label={`Move ${label} up`}
                      data-testid="run-queue-move-up"
                      disabled={!canUp || saving}
                      title={!canUp ? 'Locked in sequence' : undefined}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        move(tripId, -1);
                      }}
                    >
                      Up
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      type="button"
                      aria-label={`Move ${label} down`}
                      data-testid="run-queue-move-down"
                      disabled={!canDown || saving}
                      title={!canDown ? 'Locked in sequence' : undefined}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        move(tripId, 1);
                      }}
                    >
                      Down
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function tripLabel(trip: Trip & { tripNumber?: string }) {
  return trip.reference || trip.tripNumber || trip.id.slice(0, 8);
}

function TripCard({
  trip,
  onClick,
  draggable,
  releasing,
  onRelease,
  onDragStart,
  onDragEnd,
  carriedOverFrom,
}: {
  trip: Trip;
  onClick: () => void;
  carriedOverFrom?: string | null;
  draggable?: boolean;
  releasing?: boolean;
  onRelease?: () => void;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: () => void;
}) {
  const pressureClass = trip.timePressure === 'urgent' ? 'urgent' : trip.timePressure === 'warning' ? 'warning' : '';

  return (
    <div
      className={`trip-card ${pressureClass}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      role="link"
      tabIndex={0}
      aria-label={`Open ${tripLabel(trip)}${trip.clientName ? ` for ${trip.clientName}` : ''}`}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="trip-ref">{tripLabel(trip)}</div>
      {carriedOverFrom && (
        <div className="text-warning" style={{ fontSize: '0.6875rem', fontWeight: 600 }}>
          Still running from {formatDate(carriedOverFrom)}
        </div>
      )}
      <div className="trip-client">{trip.clientName ?? '—'}</div>
      <div className="trip-route">{trip.origin ?? '?'} → {trip.destination ?? '?'}</div>
      <div className="trip-meta">
        <StatusBadge status={trip.status} size="sm" />
        {trip.vehiclePlate && <span className="text-muted" style={{ fontSize: '0.75rem' }}>{trip.vehiclePlate}</span>}
      </div>
      {onRelease && (
        <div className="trip-actions">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={releasing}
            onClick={(e) => {
              e.stopPropagation();
              onRelease();
            }}
          >
            Dispatch
          </Button>
        </div>
      )}
    </div>
  );
}

function FleetStrip({ vehicles }: { vehicles: Array<Vehicle & { plateNumber?: string }> }) {
  return (
    <div className="fleet-strip">
      <h4>Fleet ({vehicles.length})</h4>
      {vehicles.length === 0 ? (
        <p className="text-muted" style={{ fontSize: '0.8125rem' }}>No vehicles registered</p>
      ) : (
        vehicles.map((v) => (
          <div key={v.id} className="fleet-item">
            <div className="fleet-plate">{v.plate || v.plateNumber || '—'}</div>
            <div className="fleet-status">{humanize(v.status)} · {v.driverName ?? 'No driver'}</div>
          </div>
        ))
      )}
    </div>
  );
}
